import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  FeatureCard,
  CTASection,
} from "../../components/marketing-components.js";

export function RecurringMonitoringPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Recurring accessibility monitoring"
        subtitle="Monthly scans that track progress, detect new issues, and prove commitment"
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>Why monthly monitoring beats one-off audits</h2>

          <p>
            A single accessibility audit is a snapshot. Your website changes
            constantly. Monthly monitoring keeps you ahead of new issues and
            proves you're actively improving.
          </p>

          <div className="features-grid features-grid--three">
            <FeatureCard
              title="Catch new issues early"
              description="When templates change or new pages launch, you know immediately."
            />
            <FeatureCard
              title="Track progress"
              description="See exactly which issues you've fixed and which still need work."
            />
            <FeatureCard
              title="Proof of effort"
              description="Month-to-month reports demonstrate good faith accessibility commitment."
            />
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>How it works</h2>

          <div className="timeline">
            <div className="timeline-item">
              <h4>Month 1: Baseline</h4>
              <p>
                Initial comprehensive scan. You get your first report and
                accessibility statement. Top issues identified.
              </p>
            </div>

            <div className="timeline-item">
              <h4>Month 2: Verification</h4>
              <p>
                Same pages scanned again. You see: which issues were fixed,
                which remain, and any new issues that appeared.
              </p>
            </div>

            <div className="timeline-item">
              <h4>Month 3+: Ongoing tracking</h4>
              <p>
                Continue monthly until clean. Then optionally go quarterly to
                maintain baseline.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>What changes between scans</h2>

          <div className="comparison">
            <div>
              <h4>Site-level changes detected</h4>
              <ul>
                <li>New pages launched</li>
                <li>Template redesigns</li>
                <li>Component library updates</li>
                <li>Theme or design changes</li>
                <li>Nav structure updates</li>
              </ul>
            </div>

            <div>
              <h4>Issue-level tracking</h4>
              <ul>
                <li>New issues introduced</li>
                <li>Resolved issues removed</li>
                <li>Persisting issues flagged</li>
                <li>Remediation timeline progress</li>
                <li>Score improvement/decline</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Real example: Tracking 6 months</h2>

          <table className="progress-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Total issues</th>
                <th>Fixed this month</th>
                <th>New this month</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>M1 (Baseline)</td>
                <td>47</td>
                <td>—</td>
                <td>—</td>
                <td>52%</td>
              </tr>
              <tr>
                <td>M2</td>
                <td>31</td>
                <td>18</td>
                <td>2</td>
                <td>69%</td>
              </tr>
              <tr>
                <td>M3</td>
                <td>12</td>
                <td>21</td>
                <td>2</td>
                <td>87%</td>
              </tr>
              <tr>
                <td>M4</td>
                <td>8</td>
                <td>5</td>
                <td>1</td>
                <td>91%</td>
              </tr>
              <tr>
                <td>M5</td>
                <td>3</td>
                <td>6</td>
                <td>1</td>
                <td>96%</td>
              </tr>
              <tr>
                <td>M6</td>
                <td>0</td>
                <td>3</td>
                <td>0</td>
                <td>100%</td>
              </tr>
            </tbody>
          </table>

          <p style={{ marginTop: "16px", fontStyle: "italic" }}>
            6 months of data. Steady progress. Clear proof of commitment.
            Perfect for legal or insurance documentation.
          </p>
        </div>
      </section>

      <CTASection
        title="Start monthly monitoring"
        description="All plans include automatic monthly scans. No setup required."
        primaryCTA={{ label: "Get started", href: "/sign-in" }}
      />
    </PublicLayout>
  );
}
